import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useSettings } from '../hooks/useSettings';
import { useSession } from '../hooks/useSession';
import { api } from '../lib/api';
import { disablePush, enablePush, getPushStatus, hasActiveSubscription, isPushSupported } from '../lib/push';
import { InstallButton } from '../components/InstallButton';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

function InstallSection() {
  const { canInstall, isIos, installed } = useInstallPrompt();
  return (
    <section className="panel p-4 mt-4">
      <h2 className="section-label mb-1">Install app</h2>
      {installed ? (
        <p className="text-sm text-ink-300">Durak is installed on this device. ✨</p>
      ) : (
        <>
          <p className="text-sm text-ink-300 mb-3">
            Add Durak to your home screen for a full-screen, app-like experience with push notifications.
          </p>
          <InstallButton
            autoHide={false}
            label={isIos ? 'How to install on iOS' : canInstall ? 'Add to Home Screen' : 'Install via browser menu'}
          />
          {!canInstall && !isIos && (
            <p className="text-[11px] text-ink-300 mt-2">
              Your browser hasn't offered an install prompt yet. Try using the browser's menu — Chrome &amp; Edge: ⋮ → <em>Install app</em>. Firefox supports it via the address-bar icon on supported sites.
            </p>
          )}
        </>
      )}
    </section>
  );
}

export default function SettingsPage() {
  const s = useSettings();
  const user = useSession(st => st.user);
  const setUser = useSession(st => st.setUser);
  const nav = useNavigate();
  const [pushPerm, setPushPerm] = useState<'granted' | 'denied' | 'default' | 'unsupported'>('default');
  const [pushHasSub, setPushHasSub] = useState<boolean>(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);

  async function refreshPushState() {
    const perm = await getPushStatus();
    setPushPerm(perm);
    setPushHasSub(perm === 'granted' ? await hasActiveSubscription() : false);
  }
  useEffect(() => { isPushSupported().then(refreshPushState); }, []);

  // True only when permission AND an actual PushSubscription exist. The
  // browser-level permission alone isn't enough — invites silently no-op
  // unless the server has a subscription row for this user.
  const pushActive = pushPerm === 'granted' && pushHasSub;

  async function togglePush() {
    setBusy(true); setMsg(null);
    try {
      if (pushActive) {
        await disablePush();
        setMsg('Push notifications disabled');
      } else {
        const r = await enablePush();
        if (!r.ok) setMsg(r.reason || 'Could not enable push');
        else setMsg('Push notifications enabled');
      }
      await refreshPushState();
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <h1 className="font-display text-2xl font-bold mb-4">Settings</h1>

      <section className="panel p-4 space-y-3">
        <h2 className="section-label">Gameplay</h2>
        <Toggle label="Rotate seats around the table" value={s.rotateSeats} onChange={v => s.set('rotateSeats', v)} />
        <Toggle label="Sound effects" value={s.sound} onChange={v => s.set('sound', v)} />
        <Toggle label="Haptic feedback" value={s.haptic} onChange={v => s.set('haptic', v)} />
        <Toggle label="Show hints" value={s.showHints} onChange={v => s.set('showHints', v)} />
        <Toggle label="High-contrast mode" value={s.highContrast} onChange={v => s.set('highContrast', v)} />
      </section>

      <section className="panel p-4 mt-4 space-y-3">
        <h2 className="section-label">Notifications</h2>
        <p className="text-sm text-ink-300">Get push notifications when friends invite you to a game.</p>
        <div className="flex items-center gap-3">
          <span className="text-sm">Status:
            <span className={`ml-2 ${pushActive ? 'text-emerald-400' : pushPerm === 'denied' ? 'text-rose-400' : 'text-ink-200'}`}>
              {pushActive
                ? 'enabled'
                : pushPerm === 'granted'
                  ? 'permission granted, no subscription'
                  : pushPerm}
            </span>
          </span>
          <button className="btn-primary !py-1.5 ml-auto" disabled={busy || pushPerm === 'unsupported' || pushPerm === 'denied'} onClick={togglePush}>
            {pushActive ? 'Disable' : pushPerm === 'granted' ? 'Re-subscribe' : 'Enable'}
          </button>
        </div>
        {msg && <div className="text-sm text-ink-200">{msg}</div>}
      </section>

      <InstallSection />

      <section className="panel p-4 mt-4 border-rose-500/30">
        <h2 className="section-label mb-1 text-rose-300">Danger zone</h2>
        <p className="text-sm text-ink-300 mb-3">
          Delete your account and all associated data (games, stats, push subscriptions).
          This cannot be undone.
        </p>
        {!showDelete ? (
          <button className="btn-secondary !border-rose-500/50 !text-rose-300" onClick={() => setShowDelete(true)}>
            Delete my account
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-sm">Type <span className="font-mono text-rose-300">DELETE</span> to confirm:</p>
            <input
              className="input"
              autoFocus
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
            />
            {delErr && <div className="text-rose-400 text-sm">{delErr}</div>}
            <div className="flex gap-2">
              <button
                className="btn-secondary !border-rose-500/60 !bg-rose-500/20 !text-rose-100"
                disabled={confirmText !== 'DELETE' || delBusy}
                onClick={async () => {
                  setDelBusy(true); setDelErr(null);
                  try {
                    await api.deleteAccount();
                    setUser(null);
                    nav('/login', { replace: true });
                  } catch (e: any) {
                    setDelErr(e.message || 'Could not delete account');
                    setDelBusy(false);
                  }
                }}
              >
                {delBusy ? 'Deleting…' : 'Permanently delete'}
              </button>
              <button className="btn-ghost" onClick={() => { setShowDelete(false); setConfirmText(''); setDelErr(null); }}>
                Cancel
              </button>
            </div>
            {user && !user.isGuest && (
              <p className="text-xs text-ink-300 mt-2">
                Note: this removes your account from Durak. To also revoke the
                connected sign-in provider (Google / Facebook), visit that
                provider's connected-apps settings.
              </p>
            )}
          </div>
        )}
      </section>
    </AppShell>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full transition relative ${value ? 'bg-felt-500' : 'bg-ink-600'}`}
        aria-pressed={value}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  );
}
